// Phase 8 Plan 03 Task 1 — closed-registry walker dispatcher.
//
// Pure TS — no React, no DOM. Server-friendly: Phase 10 AI prompt-gen + Phase 13 SSR
// re-import from here. Mirrors apps/web/src/widgets/template-renderers.ts shape:
// Map<TemplateId, walkerFn> wrapped in Object.freeze, exported as ReadonlyMap.
//
// Adding a new template (per docs/ADD-A-TEMPLATE.md Plan 12):
//   1. Add the templateId literal to packages/shared/src/schemas/widget-templates-catalog.ts
//   2. Add the typed configSchema under packages/shared/src/schemas/widget-templates/
//   3. Write the per-template walker in packages/shared/src/forms/per-template/<id>.ts
//   4. Add a Map entry HERE — that's the entire walker-dispatcher surface.
//
// Phase 7 D-17 four-layer WIDG-10 defense (mirrored from template-renderers.ts):
//   Layer 1 (compile-time):   `ReadonlyMap<TemplateId, walkerFn>` type erases .set / .delete
//   Layer 2 (runtime throw):  not applicable here (no plugin loader on the walker tier)
//   Layer 3 (runtime freeze): `Object.freeze(_walkers)` — THIS FILE
//   Layer 4 (static analysis): grep gate — see SUMMARY.md deviations for scope note
//
// Critical correctness note (RESEARCH §"Pitfall 2", cross-ref template-renderers.ts):
// Object.freeze on a Map does NOT block .set() or .delete() because Map internals
// live in slots, not own properties. The ReadonlyMap type erasure (Layer 1) blocks
// at compile time; the dispatcher_test.ts uses @ts-expect-error to assert the type
// erasure remains in place.
//
// shopping-list-aggregator is Phase 11-owned (Phase 01.1 D-28) — absent by design.
// The walkers Map has EXACTLY 6 entries; the unknown-template branch in
// walkWidgetConfig returns an empty group for forward-compat with future templates
// (intentional graceful degradation per RESEARCH lines 446-451).

import type { TemplateId } from "../../schemas/widget-templates-catalog.js";
import type { FieldDescriptor } from "../descriptors.js";
import { walkBranchVariantConfig } from "./branch-variant.js";
import { walkOptionSetConfig } from "./option-set.js";
import { walkRankedTierConfig } from "./ranked-tier.js";
import { walkSizeAndYieldConfig } from "./size-and-yield.js";
import { walkStaticNoteConfig } from "./static-note.js";
import { walkSummaryChecklistConfig } from "./summary-checklist.js";

// All six per-template walkers; "shopping-list-aggregator" is owned by Phase 11
// (Phase 01.1 D-28 catalog ownership table) — absent by design.
const _walkers = new Map<TemplateId, (configPath: string) => FieldDescriptor>([
  ["option-set", walkOptionSetConfig],
  ["summary-checklist", walkSummaryChecklistConfig],
  ["static-note", walkStaticNoteConfig],
  ["size-and-yield", walkSizeAndYieldConfig],
  ["ranked-tier", walkRankedTierConfig],
  ["branch-variant", walkBranchVariantConfig],
]);

// Phase 7 D-17 Layer 3: freeze the Map shell.
// Belt-and-suspenders alongside the ReadonlyMap type (Layer 1) and the test
// gate's @ts-expect-error on `walkers.set` (Layer 4 equivalent).
export const walkers: ReadonlyMap<TemplateId, (configPath: string) => FieldDescriptor> =
  Object.freeze(_walkers);

export function walkWidgetConfig(templateId: TemplateId, configPath: string): FieldDescriptor {
  const walker = walkers.get(templateId);
  if (walker === undefined) {
    // Forward-compat: unknown templateId returns a safe empty group rather than
    // throwing. Matches RESEARCH §"Pattern 1" lines 446-451 — Phase 11's
    // shopping-list-aggregator landing later can ship without breaking earlier
    // Phase-8 callers. The author UI renders an empty config section in this case.
    return { kind: "group", name: "config", path: configPath, children: [] };
  }
  return walker(configPath);
}
